import { css, html, LitElement, nothing } from 'lit'
import { property, state } from 'lit/decorators.js'
import { safeCustomElement } from '@/utilities/custom-element'
import type { SelectFieldOption, SelectFieldStyle } from './types'

let idCounter = 0
function generateId(): string {
  return `mdc-select-field-${++idCounter}`
}

@safeCustomElement('mdc-select-field')
export class MdcSelectField extends LitElement {
  static formAssociated = true

  @property({ type: String }) variant: SelectFieldStyle = 'secondary'
  @property({ type: String }) label = ''
  @property({ type: String }) hint = ''
  @property({ type: String }) placeholder = ''
  @property({ type: String }) value = ''
  @property({ type: String }) name = ''
  @property({ type: String }) id = ''
  @property({ type: Boolean }) disabled = false
  @property({ type: Boolean }) required = false
  @property({ type: Array }) options: SelectFieldOption[] = []
  @property({ type: String, attribute: 'validation' }) validationStr = ''
  @state() private _childOptions: SelectFieldOption[] = []

  private selectId = generateId()
  private _observer: MutationObserver | null = null
  private _internals!: ElementInternals

  constructor() {
    super()
    this._internals = this.attachInternals()
  }

  get validation() {
    if (!this.validationStr) return null
    try {
      return JSON.parse(this.validationStr)
    } catch {
      return null
    }
  }

  set validation(value) {
    this.validationStr = value ? JSON.stringify(value) : ''
  }

  static styles = css`
		:host {
			display: block;
			font-family: var(--typography-font-family-sans);
		}

		.mdc-select-field-wrapper {
			--_mdc-select-field__input-padding-sides: var(--form-field-padding-horizontal, 0.75rem);
			--_mdc-select-field__input-padding-vertical: var(--form-field-padding-vertical, 0.5rem);
			--_mdc-select-field-chevron-size: var(--form-field-icon-size, 1.125rem);

			display: flex;
			flex-direction: column;
			gap: var(--spacing-050, 0.5rem);
		}

		.mdc-select-field__label {
			font-size: var(--text-075, 0.75rem);
			color: var(--color-functional-gray-400);
			font-weight: 450;
			line-height: 1;
		}

		.mdc-select-field__select {
			box-sizing: border-box;
			appearance: none;
			margin: 0;
			padding: 0;
			border: 0;
			background: none;
			display: flex;
			align-items: center;
			align-self: stretch;
			width: 100%;
			height: var(--form-field-height, 2.75rem);
			font-size: var(--form-field-font-size, 0.875rem);
			color: var(--color-functional-gray-black);
			font-weight: 450;
			line-height: 1;
			border: 1px solid transparent;
			border-radius: var(--radius-md, 0.5rem);
			padding: var(--_mdc-select-field__input-padding-vertical) var(--_mdc-select-field__input-padding-sides);
			padding-right: calc(var(--_mdc-select-field__input-padding-sides) + var(--_mdc-select-field-chevron-size) + var(--spacing-050, 0.5rem));
			background-color: var(--color-functional-gray-white);
			background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
			background-repeat: no-repeat;
			background-position: right var(--_mdc-select-field__input-padding-sides) center;
			cursor: pointer;
		}

		/* breakpoints.md - 1 */
		@media (max-width: 767px) {
			.mdc-select-field__select {
				font-size: var(--form-field-font-size-mobile, 1rem);
			}
		}

		.mdc-select-field__select:focus-visible {
			box-shadow: 0 0 0 2px var(--color-functional-blue-300);
			outline: none;
		}

		.mdc-select-field__hint {
			font-size: var(--text-075, 0.75rem);
			color: var(--color-functional-gray-400);
			font-weight: 450;
			line-height: 1.3;
		}

		.mdc-select-field-wrapper--validation-error .mdc-select-field__select {
			border-color: var(--color-functional-red-500);
		}

		.mdc-select-field-wrapper--validation-error .mdc-select-field__select:focus-visible {
			box-shadow: 0 0 0 2px rgba(var(--color-functional-red-500-rgb, 239, 68, 68), 0.5);
			outline: none;
		}

		.mdc-select-field-wrapper--validation-error .mdc-select-field__hint {
			color: var(--color-functional-red-500);
		}

		.mdc-select-field__select:disabled {
			color: var(--color-functional-gray-300);
			background-color: var(--color-functional-gray-50);
			cursor: not-allowed;
		}

		.mdc-select-field-wrapper--disabled .mdc-select-field__label {
			color: var(--color-functional-gray-400);
		}

		.mdc-select-field-wrapper--style-secondary .mdc-select-field__select {
			border-color: var(--color-functional-gray-100);
		}

		.mdc-select-field-wrapper--style-tertiary .mdc-select-field__select {
			transition: background-color 0.2s ease;
		}

		.mdc-select-field-wrapper--style-tertiary .mdc-select-field__select:hover:not(:disabled),
		.mdc-select-field-wrapper--style-tertiary .mdc-select-field__select:focus-visible:not(:disabled) {
			background-color: var(--color-functional-gray-25);
		}

		.mdc-select-field-wrapper--style-tertiary .mdc-select-field__select:disabled {
			background-color: var(--color-functional-gray-50);
		}
	`

  private _readChildOptions() {
    this._childOptions = Array.from(this.querySelectorAll(':scope > option')).map((opt) => ({
      value: (opt as HTMLOptionElement).value,
      label: opt.textContent?.trim() || '',
    }))
  }

  connectedCallback() {
    super.connectedCallback()
    if (this.id) {
      this.selectId = this.id
    } else if (!this.selectId) {
      this.selectId = generateId()
    }

    this._readChildOptions()

    this._observer = new MutationObserver(() => this._readChildOptions())
    this._observer.observe(this, { childList: true, subtree: true, characterData: true })

    this._internals.setFormValue(this.value || null)
  }

  updated(changedProperties: Map<PropertyKey, unknown>) {
    super.updated(changedProperties)
    if (changedProperties.has('value') || changedProperties.has('name')) {
      this._internals.setFormValue(this.value || null)
      this._updateValidity()
    }
  }

  private _updateValidity() {
    const select = this.shadowRoot?.querySelector('select')
    if (select) {
      this._internals.setValidity(select.validity, select.validationMessage, select)
    }
  }

  formResetCallback() {
    this.value = ''
    this._internals.setFormValue(null)
  }

  formDisabledCallback(disabled: boolean) {
    this.disabled = disabled
  }

  formStateRestoreCallback(state: string) {
    this.value = state
    this._internals.setFormValue(this.value || null)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this._observer?.disconnect()
    this._observer = null
  }

  private handleChange(e: Event) {
    const select = e.target as HTMLSelectElement
    this.value = select.value
    this._internals.setFormValue(this.value || null)
    this._updateValidity()
    this.dispatchEvent(
      new CustomEvent('change', {
        detail: { value: this.value, originalEvent: e },
        bubbles: true,
        composed: true,
      })
    )
  }

  render() {
    const validation = this.validation
    const classes = [
      'mdc-select-field-wrapper',
      `mdc-select-field-wrapper--style-${this.variant}`,
      validation ? `mdc-select-field-wrapper--validation-${validation.status}` : '',
      this.disabled ? 'mdc-select-field-wrapper--disabled' : '',
    ]
      .filter(Boolean)
      .join(' ')

    return html`
			<div class=${classes}>
				${this.label ? html`<label for=${this.selectId} class="mdc-select-field__label">${this.label}</label>` : nothing}
				<select
					id=${this.selectId}
					class="mdc-select-field__select"
					name=${this.name || ''}
					?disabled=${this.disabled}
					?required=${this.required}
					@change=${this.handleChange}
				>
					${this.placeholder ? html`<option value="">${this.placeholder}</option>` : nothing}
					${this.options.map(
            (opt) =>
              html`<option value=${opt.value} ?selected=${opt.value === this.value}>${opt.label}</option>`
          )}
					${this._childOptions.map(
            (opt) =>
              html`<option value=${opt.value} ?selected=${opt.value === this.value}>${opt.label}</option>`
          )}
				</select>
				${this.hint ? html`<span class="mdc-select-field__hint">${this.hint}</span>` : nothing}
			</div>
		`
  }
}
